import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Link } from 'react-router-dom';
import { Box, Card, CardHeader, Chip, IconButton, Link as MuiLink } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { Add, Edit } from '@mui/icons-material';
import { typeCurrency, typeDate } from 'components/mui/Formatters';
import { isFProject } from 'config/project';
import { VoyageModel, VoyageStatus } from 'models/VoyageModel';
import { CompanyType } from 'models/CompanyModel';
import useVoyages from 'modules/voyages/hooks/useVoyages';
import useVoyageDocs from 'modules/voyages/hooks/useVoyageDocs';
import useVoyageModals from 'modules/voyages/hooks/useVoyageModals';
import auth from 'recoil/atoms/auth';
import voyageFamily from 'recoil/atoms/voyageFamily';


export type VoyagesViewProps = {
    status: VoyageStatus,
}

const departed = [VoyageStatus.Departed, VoyageStatus.Arrived];
const hasED = [VoyageStatus.Loaded, VoyageStatus.Departed, VoyageStatus.Arrived];

const TableInner = ({status}: VoyagesViewProps) => {
    const {
        openAddVoyageModal,
        openEditVoyageModal,
        openCancelVoyageModal,
        //openLostVoyageModal,
        openVoyageNoteModal,
        openToyamaInvoicePackagesModal,
        openJpInvoicePackagesModal,
        loadVoyageWithConfirmation,
    } = useVoyageModals();

    const {
        voyageDeparted,
        voyageArrived,
    } = useVoyages();

    const {
        downloadManifest,
        downloadBLs,
        downloadECs,
        downloadInvoices,
        downloadSI,
        downloadDismantledList,
        downloadDocPackage,
        downloadED,
        downloadCargoLists,
        downloadTallymanList,
        downloadDeliveryList,
    } = useVoyageDocs();

    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const voyages = useRecoilValue(voyageFamily(status));

    const filtered = my.type === CompanyType.Shipper ? voyages.filter(v => v.cars.total) : voyages;

    const addButton = my.permissions.has('voyages create') ? (
        <IconButton
            aria-label="add voyage"
            onClick={openAddVoyageModal}
        >
            <Add/>
        </IconButton>
    ) : null;

    const editButton = (voyage: VoyageModel) => (
        <GridActionsCellItem
            icon={<Edit/>}
            label={t('edit')}
            onClick={() => openEditVoyageModal(voyage)}
        />
    );

    const manageButton = (voyage: VoyageModel, label: string, action: (id: number) => void) => (
        <GridActionsCellItem
            label={label}
            onClick={() => action(voyage.id)}
            showInMenu
        />
    );

    const columns: GridColDef<VoyageModel>[] = [
        {
            field: 'vesselId',
            headerName: t('vessel'),
            flex: 1,
            valueGetter: params => params.row.vessel.name,
            minWidth: 110,
        },
        {
            field: 'number',
            headerName: t('voyage_no'),
            flex: 1,
            minWidth: 115,
            renderCell: params => <MuiLink href={`/voyage/${params.row.id}`}>{params.value}</MuiLink>
        },
        {
            field: 'pol',
            headerName: t('port'),
            flex: 1,
            valueGetter: params => params.row.pol.name,
            minWidth: 100,
        },
        {
            field: departed.includes(status) ? 'departedAt' : 'loadingStartDate',
            headerName: departed.includes(status) ? t('departed') : t('loading'),
            flex: 1,
            minWidth: 100,
            ...typeDate,
        },
        {
            field: 'pod',
            headerName: t('destination'),
            flex: 1,
            valueGetter: params => `${params.row.pod.code}, ${params.row.pod.country}`,
            minWidth: 100,
        },
        {
            field: status === VoyageStatus.Arrived ? 'arrivedAt' : 'eta',
            headerName: status === VoyageStatus.Arrived ? 'Arrived' : 'ETA',
            flex: 1,
            minWidth: 100,
            ...typeDate,
        },
        ...my.type !== CompanyType.PortAgent && !isFProject ? [{
            field: 'freightPrice',
            headerName: t('freight'),
            ...typeCurrency('usd'),
            width: 90,
        }] : [],
        {
            field: 'cutDate',
            headerName: t('cut_date'),
            flex: 1,
            minWidth: 100,
            ...typeDate,
        },
        {
            field: 'blIssueDate',
            headerName: t('ru_bl_issue_date'),
            flex: 1,
            minWidth: 100,
            ...typeDate,
        },
        ...(my.type === CompanyType.Master || status === VoyageStatus.Canceled) ? [{
            field: 'note',
            headerName: t('note'),
            width: 100,
        }] : [],
        ...status !== VoyageStatus.Canceled ? [{
            field: 'allowDismantled',
            headerName: '[R]',
            type: 'boolean',
            renderCell: params => params.value ? <Chip
                label={`R ${params.row.cars.dis}`}
                color="error"
                variant="outlined"
                size="small"
            /> : null,
            width: 80,
        },
            {
                field: 'carsTotal',
                headerName: t('cars'),
                type: 'number',
                width: 90,
                valueGetter: params => params.row.cars.total,
                renderCell: params => <Chip
                    sx={{cursor: 'pointer'}}
                    label={my.type === CompanyType.Master ? `${params.value}/${params.row.cars.capacity}` : params.value}
                    color={params.value > params.row.cars.capacity
                        ? 'error'
                        : params.value >= params.row.cars.capacity - 10
                            ? 'warning' : 'success'}
                    variant={params.value ? undefined : 'outlined'}
                    size="small"
                    component={Link}
                    to={`/voyage/${params.row.id}`}
                />
            },
            ...my.permissions.has('invoices create') && !isFProject ? [{
                field: 'carsPaid',
                headerName: t('paid_by_type'),
                type: 'number',
                width: 100,
                sortable: false,
                valueGetter: params => params.row.cars,
                renderCell: params => (
                    <>
                        <Chip
                            sx={{cursor: 'pointer', mr: '4px'}}
                            label={params.value.paidCaf}
                            color={params.value.caf === 0 || params.value.caf === params.value.paidCaf ? 'success' : 'error'}
                            variant={params.value.caf === 0 ? 'outlined' : undefined}
                            size="small"
                            component={Link}
                            to={`/voyage/${params.row.id}/finances`}
                        />
                        <Chip
                            sx={{cursor: 'pointer'}}
                            label={params.value.paidFob}
                            color={params.value.fob === 0 || params.value.fob === params.value.paidFob ? 'success' : 'error'}
                            variant={params.value.fob === 0 ? 'outlined' : undefined}
                            size="small"
                            component={Link}
                            to={`/voyage/${params.row.id}/finances`}
                        />
                    </>
                ),
            }] as GridColDef<VoyageModel>[] : [],
            {
                field: 'documents',
                headerName: t('docs'),
                type: 'actions',
                getActions: params => {
                    const v = params.row;
                    const act = [];

                    if (my.type === CompanyType.Master && status !== VoyageStatus.Lost) {
                        act.push(manageButton(v, t('full_jp_package'), downloadDocPackage()));
                        if (!isFProject)
                            act.push(manageButton(v, t('jp_bls'), downloadBLs('jp')));

                        if (v.pol.id === 1)
                            act.push(manageButton(v, t('toyama_invoices'), openToyamaInvoicePackagesModal));
                        else if (v.pol.id === 9)
                            act.push(manageButton(v, t('naoetsu_invoices'), downloadInvoices(undefined, 'naoetsu')));
                        else
                            act.push(manageButton(v, t('jp_invoices'), openJpInvoicePackagesModal));

                        if (v.pod.country === 'KR' && (v.status === VoyageStatus.Departed || v.status === VoyageStatus.Arrived))
                            act.push(manageButton(v, t('korea_invoices'), downloadInvoices(undefined, 'korea')));

                        if (v.pol.id === 1 || v.pol.id === 4 || v.pol.id === 7) {
                            act.push(manageButton(v, t('loading_cargo_lists'), downloadCargoLists('common')));
                            act.push(manageButton(v, 'Delivery Fax Cars With Docs', downloadCargoLists('doc-no_yard')));
                            act.push(manageButton(v, 'Documents Fax Cars in Yard', downloadCargoLists('no_doc-yard')));
                        }

                        act.push(manageButton(v, t('tallyman_list'), downloadTallymanList));
                        act.push(manageButton(v, t('delivery_list'), downloadDeliveryList));

                        act.push(manageButton(v, t('jp_si'), downloadSI()));
                        act.push(manageButton(v, t('ecs'), downloadECs));

                        if (!isFProject) {
                            act.push(manageButton(v, t('ru_manifest'), downloadManifest()));
                            act.push(manageButton(v, t('ru_manifest_warehouse'), downloadManifest('warehouse')));
                            act.push(manageButton(v, t('ru_bls'), downloadBLs('ru')));
                        }
                        v.allowDismantled && act.push(manageButton(v, t('dismantled_list'), downloadDismantledList));
                    }

                    if (my.type === CompanyType.Party) {
                        //act.push(manageButton(v, t('ru_bls'), downloadBLs('ru')));
                        act.push(manageButton(v, t('ecs'), downloadECs));
                    }

                    if (my.type === CompanyType.Shipper && hasED.includes(v.status)) {
                        act.push(manageButton(v, t('bl_ed'), downloadDocPackage(my.company.id)));
                        act.push(manageButton(v, t('ed'), downloadED(my.company.id)));
                    }

                    if (my.type === CompanyType.PortAgent) {
                        act.push(manageButton(v, t('full_jp_package'), downloadDocPackage()));
                        act.push(manageButton(v, t('ecs'), downloadECs));
                    }

                    if (my.type === CompanyType.VesselOwner) {
                        act.push(manageButton(v, t('full_jp_package'), downloadDocPackage()));

                        if (v.pod.country === 'KR' && (v.status === VoyageStatus.Departed || v.status === VoyageStatus.Arrived))
                            act.push(manageButton(v, t('korea_invoices'), downloadInvoices(undefined, 'korea')));

                        act.push(manageButton(v, t('ru_manifest'), downloadManifest()));
                    }

                    return act;
                },
            },
        ] as GridColDef<VoyageModel>[] : [],
        {
            field: 'actions',
            type: 'actions',
            getActions: params => {
                const v = params.row;
                const act = [];
                if (my.permissions.has('voyages update')) {
                    if (status === VoyageStatus.Pending)
                        act.push(editButton(v));

                    act.push(
                        <GridActionsCellItem
                            label={t('edit_note')}
                            onClick={() => openVoyageNoteModal(v)}
                            showInMenu
                        />
                    );
                }

                if (my.permissions.has('cars manage') && status === VoyageStatus.Pending)
                    act.push(manageButton(v, t('voyage_loaded'), loadVoyageWithConfirmation));

                if (my.permissions.has('cars manage') && status === VoyageStatus.Loaded)
                    act.push(manageButton(v, t('voyage_departed'), voyageDeparted));

                if (my.permissions.has('cars manage') && status === VoyageStatus.Departed)
                    act.push(manageButton(v, t('voyage_arrived'), voyageArrived));

                /*
                if (my.permissions.has('cars manage') && v.status === VoyageStatus.Departed)
                    act.push(manageButton(v, 'Vessel is Lost', openLostVoyageModal));
                */

                if (my.permissions.has('voyages delete') && status === VoyageStatus.Pending)
                    act.push(manageButton(v, t('voyage_canceled'), openCancelVoyageModal));

                return act;
            },
        },
    ];

    return (
        <Card>
            <CardHeader
                title={t('voyages')}
                action={addButton}
            />
            <Box sx={{height: 'calc(100vh - 210px)', padding: '0 1rem'}}>
                <DataGrid
                    rows={filtered}
                    columns={columns}
                    disableRowSelectionOnClick
                />
            </Box>
        </Card>
    );
};

const VoyagesDesktop = (p: VoyagesViewProps) => {
    return (
        <Suspense fallback={null}>
            <TableInner {...p}/>
        </Suspense>
    )
};

export default VoyagesDesktop;
